'use client';

import { useEffect, useState } from 'react';
import { Eye } from 'lucide-react';
import NumberFlow from '@number-flow/react';
import { apiFetch } from '@/lib/api';
import DashboardMetricCard from '../DashboardMetricCard';

type MyViewers = { views: number; viewers: Array<{ personId: number }> };

export default function ViewersCard() {
  const [views, setViews] = useState<number | null>(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await apiFetch('/api/proxy/api/profile/my-viewers', undefined, { method: 'GET' });
        if (!res.ok) {
          if (!cancelled) setError(true);
          return;
        }
        const data = (await res.json()) as MyViewers;
        if (!cancelled) setViews(typeof data?.views === 'number' ? data.views : 0);
      } catch {
        if (!cancelled) setError(true);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const icon = <Eye className="h-4 w-4" strokeWidth={1.75} />;

  if (error) {
    return (
      <DashboardMetricCard title="Profile views" icon={icon}>
        <div className="text-sm text-muted-foreground">Couldn&apos;t load views right now.</div>
      </DashboardMetricCard>
    );
  }

  if (views === null) {
    return (
      <DashboardMetricCard title="Profile views" href="/profile/viewers" icon={icon}>
        <div className="space-y-3">
          <div className="h-9 w-20 rounded bg-muted animate-pulse" />
          <div className="h-3 w-32 rounded bg-muted/60 animate-pulse" />
        </div>
      </DashboardMetricCard>
    );
  }

  if (views === 0) {
    return (
      <DashboardMetricCard title="Profile views" icon={icon}>
        <div className="font-serif text-3xl font-semibold tabular-nums">0</div>
        <div className="text-sm text-muted-foreground mt-1">
          Nobody yet. Share your profile to get discovered.
        </div>
      </DashboardMetricCard>
    );
  }

  return (
    <DashboardMetricCard title="Profile views" href="/profile/viewers" icon={icon}>
      <div className="font-serif text-3xl font-semibold tabular-nums">
        <NumberFlow value={views} />
      </div>
      <div className="text-sm text-muted-foreground mt-1">
        {views === 1 ? 'view' : 'views'} in the last 30 days
      </div>
    </DashboardMetricCard>
  );
}
